import { Injectable, Inject, InjectionToken, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export const GOOGLE_MAPS_API_KEY = new InjectionToken<string>('GOOGLE_MAPS_API_KEY');

// Declare google for TypeScript
declare const google: any;
declare const window: any;

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {
  private loaded = false;
  private loadingPromise?: Promise<any>;

  constructor(
    @Inject(GOOGLE_MAPS_API_KEY) private apiKey: string,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Load the Google Maps JavaScript API
   * Returns a promise that resolves when the API is loaded
   */
  async load(): Promise<void> {
    // Skip loading on server-side
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    // If already loaded, resolve immediately
    if (this.loaded) {
      return Promise.resolve();
    }

    // If currently loading, return existing promise
    if (this.loadingPromise) {
      await this.loadingPromise;
      return;
    }

    // Check if API key is configured
    if (!this.apiKey) {
      throw new Error('Google Maps API key is not configured');
    }

    // Check if already loaded by script
    if (typeof window !== 'undefined' && window.google?.maps) {
      this.loaded = true;
      return Promise.resolve();
    }

    // Load the Google Maps script manually with proper async loading pattern
    this.loadingPromise = new Promise((resolve, reject) => {
      // Create a callback function for Google Maps to call when loaded
      const callbackName = `__googleMapsCallback_${Date.now()}`;
      (window as any)[callbackName] = () => {
        this.loaded = true;
        delete (window as any)[callbackName];
        resolve(undefined);
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&loading=async&callback=${callbackName}`;
      script.async = true;
      script.defer = true;

      script.onerror = (error) => {
        this.loadingPromise = undefined;
        delete (window as any)[callbackName];
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  /**
   * Check if Google Maps API is loaded
   */
  isLoaded(): boolean {
    return this.loaded;
  }
}
